import { makeAutoObservable, runInAction } from 'mobx';
import type { FHRequest } from '../types';
import { mentorApi, type RecallResponse } from '../api/mentor_api';

export class MentorStore {
  // Mentor queue (tasks awaiting approval)
  mentorQueue: FHRequest[] = [];
  mentorLoading = false;
  mentorCursor: string | null = null;
  mentorHasMore = true;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch mentor queue (tasks awaiting mentor approval)
   */
  async fetchMentorQueue(refresh = false): Promise<void> {
    if (this.mentorLoading) return;
    if (!refresh && !this.mentorHasMore) return;

    runInAction(() => {
      this.mentorLoading = true;
      this.error = null;
      if (refresh) {
        this.mentorCursor = null;
        this.mentorHasMore = true;
      }
    });

    try {
      const cursor = refresh ? undefined : this.mentorCursor || undefined;
      const response = await mentorApi.getQueue(cursor);

      runInAction(() => {
        if (refresh) {
          this.mentorQueue = response.items;
        } else {
          this.mentorQueue = [...this.mentorQueue, ...response.items];
        }
        this.mentorCursor = response.cursor;
        this.mentorHasMore = response.has_more;
        this.mentorLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.mentorLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch mentor queue';
      });
    }
  }

  /**
   * Claim a task for mentoring
   */
  async claimForMentor(requestKey: string): Promise<FHRequest | undefined> {
    try {
      const request = await mentorApi.claim(requestKey);

      runInAction(() => {
        // Update the request in the queue with the claimed status
        this.mentorQueue = this.mentorQueue.map((r) =>
          r.key === requestKey ? request : r
        );
      });

      return request;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to claim for mentoring';
      });
      return undefined;
    }
  }

  /**
   * Unclaim a mentor task
   */
  async unclaimMentor(requestKey: string): Promise<FHRequest | undefined> {
    try {
      const request = await mentorApi.unclaim(requestKey);

      runInAction(() => {
        // Update the request in the queue
        this.mentorQueue = this.mentorQueue.map((r) =>
          r.key === requestKey ? request : r
        );
      });

      return request;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to unclaim';
      });
      return undefined;
    }
  }

  /**
   * Approve assistant's response
   */
  async approveMentor(requestKey: string): Promise<boolean> {
    try {
      await mentorApi.approve(requestKey);

      runInAction(() => {
        // Remove from queue after approval
        this.mentorQueue = this.mentorQueue.filter((r) => r.key !== requestKey);
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to approve';
      });
      return false;
    }
  }

  /**
   * Deny assistant's response with feedback
   */
  async denyMentor(requestKey: string, reason: string): Promise<boolean> {
    try {
      await mentorApi.deny(requestKey, reason);

      runInAction(() => {
        // Remove from queue after denial
        this.mentorQueue = this.mentorQueue.filter((r) => r.key !== requestKey);
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to deny';
      });
      return false;
    }
  }

  /**
   * Recall a submitted response before a mentor claims it
   * Allows the assistant to edit their response
   * Returns the recall response with the original response body for editing
   */
  async recallResponse(requestKey: string): Promise<RecallResponse | undefined> {
    try {
      const recallResponse = await mentorApi.recall(requestKey);

      runInAction(() => {
        // Remove from mentor queue since it's no longer awaiting approval
        this.mentorQueue = this.mentorQueue.filter((r) => r.key !== requestKey);
      });

      return recallResponse;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to recall response';
      });
      return undefined;
    }
  }

  /**
   * Get the next unclaimed task from the in-memory queue, excluding a given request key.
   */
  getNextTask(excludeKey: string): FHRequest | null {
    return this.mentorQueue.find(
      (r) => r.key !== excludeKey && !r.mentor_claimed
    ) ?? null;
  }

  /**
   * Remove a request from the in-memory queue by key.
   */
  removeFromQueue(requestKey: string): void {
    this.mentorQueue = this.mentorQueue.filter((r) => r.key !== requestKey);
  }

  /**
   * Reset store state
   */
  reset(): void {
    this.mentorQueue = [];
    this.mentorCursor = null;
    this.mentorHasMore = true;
    this.error = null;
  }
}

export const mentorStore = new MentorStore();
